import { Messages } from '../../shared/messages';
import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';

import { REDUX_CACHE, HTTP_METHOD } from './constants';
import { LocalStorageKey, LocalStorageService } from '../local-storage';
import {
  errorToastMessage,
  successToastMessage,
} from '../../components/layout/toast-root/toast';

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/dist/query/react';
import { useFirebaseAuth } from '../../hooks/useFirebaseAuth';
import config from '../../envConfig';

export type CommonError = {
  message: string;
  status: number;
};

export type BaseQueryFnType = BaseQueryFn<
  string | FetchArgs,
  unknown,
  CommonError,
  any,
  FetchBaseQueryMeta
>;

export const TransformedResponseWithNotification = (
  customMessage?: string,
  mergeMessage = true,
  showErrorToastMessage = true,
) => {
  return {
    transformResponse: (response: any, meta: any): any => {
      let message = '';

      switch (meta.request.method) {
        case HTTP_METHOD.POST:
          message = mergeMessage
            ? `${customMessage || 'Data'} ${Messages.dataCreated}`
            : customMessage || `Data ${Messages.dataCreated}`;
          break;
        case HTTP_METHOD.PUT:
          message = mergeMessage
            ? `${customMessage || 'Data'} ${Messages.dataUpdated}`
            : customMessage || `Data ${Messages.dataUpdated}`;
          break;
        case HTTP_METHOD.DELETE:
          message = mergeMessage
            ? `${customMessage || 'Data'} ${Messages.dataDeleted}`
            : customMessage || `Data ${Messages.dataDeleted}`;
          break;
      }

      successToastMessage(message);
      return { id: response?.id, status: meta?.response?.status };
    },
    transformErrorResponse: (response: any, meta: any): any => {
      const error = getBaseQueryError(response);

      if (showErrorToastMessage) {
        errorToastMessage(error?.message);
      }

      return { message: error?.message, status: meta?.response?.status };
    },
  };
};

export const TransformedErrorResponseWithNotification = () => {
  return {
    transformErrorResponse: (response) => {
      const error = getBaseQueryError(response);
      errorToastMessage(error?.message);

      return error;
    },
  };
};

export const getErrorFromResponse = (resp) => {
  if (resp.status === 404 && !resp.data?.message) {
    return Messages.notFound;
  } else if (resp.status === 403 && !resp.data?.message) {
    return Messages.accessDenied;
  }

  if (resp.status < 500 && resp.data?.message) {
    return resp.data?.message;
  }

  return Messages.someWrong;
};

export const getBaseQueryError = (resp) => {
  const error = {
    message: getErrorFromResponse(resp),
    status: resp.status,
  };

  return error;
};

export const getListProvidesTags = (data, tag, deepFieldName = '') => {
  return data
    ? [
        ...data.map((item) => ({
          type: tag,
          id: deepFieldName ? item[deepFieldName].id : item.id,
        })),
        { type: tag, id: REDUX_CACHE.LIST },
      ]
    : [{ type: tag, id: REDUX_CACHE.LIST }];
};

const BASE_URL = config.apiBaseUrl;

export const callApiEndpoint = (uri) => BASE_URL + uri;

/** Optional settings for the underlying fetch (e.g. long-running admin sync calls). */
export type GetBaseQueryOptions = {
  /** Max time in ms before the client aborts the request (RTK Query fetchBaseQuery). */
  timeout?: number;
};

export function getBaseQuery(urlPart = '', options?: GetBaseQueryOptions) {
  const baseQuery = <BaseQueryFnType>fetchBaseQuery({
    baseUrl: callApiEndpoint(urlPart),
    prepareHeaders: getPrepareHeaders(),
    ...options,
  });

  // eslint-disable-next-line
  const { getToken } = useFirebaseAuth();

  return async (args, api, extraOptions) => {
    const updatedArgs = { ...args, url: getUrlWithLanguage(args.url) };
    let result = await baseQuery(updatedArgs, api, extraOptions);

    if (result.error && result.error.status === 401) {
      // token should refresh automatically. Let's make one more attempt
      const refreshedToken = await getToken();
      if (refreshedToken) {
        result = await baseQuery(updatedArgs, api, extraOptions);
      }
    } else if (result.error) {
      return result;
    }

    return result;
  };
}

export function getPrepareHeaders() {
  return async (headers) => {
    // eslint-disable-next-line
    const { getToken } = useFirebaseAuth();
    const token = await getToken();

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  };
}

export function getUrlWithLanguage(urlDefault) {
  const language = String(
    LocalStorageService.getValue(LocalStorageKey.language) ?? '',
  ).trim();

  if (!language) {
    return urlDefault;
  }

  const [path, query = ''] = urlDefault.split('?');
  const queryParams = new URLSearchParams(query);

  queryParams.set('language', language);

  const queryString = queryParams.toString();
  return queryString ? `${path}?${queryString}` : path;
}

export function getUrlParamsString(params: { [key: string]: string | number }) {
  return Object.entries(params)
    .filter(([key, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}
